/**
 * MR-Options — forward paper-trades the SENSEX 1-min RSI+BB mean-reversion strategy as
 * ATM weekly options, recording REAL live premiums. Validates whether the spot edge
 * survives as an options trade. SENSEX only, paper.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "/api/zerodha";
const fmtTime = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-";

function exportCSV(trades) {
  if (!trades.length) return;
  const h = "Side,OptType,Strike,Symbol,Qty,EntryPrem,ExitPrem,IdxEntry,IdxStop,IdxTarget,RSI,P&L,Result,ExitReason,EntryTime,ExitTime\n";
  const rows = trades.map(t => `${t.side},${t.opt_type},${t.strike},${t.symbol},${t.qty},${t.entry_premium},${t.exit_premium ?? ""},${t.entry_idx},${t.stop_idx},${t.target_idx},${t.rsi},${t.pnl || 0},${t.result || "OPEN"},${t.exit_reason || ""},${t.entry_time},${t.exit_time || ""}`).join("\n");
  const b = new Blob([h + rows], { type: "text/csv" }); const a = document.createElement("a");
  a.href = URL.createObjectURL(b); a.download = "mr-options-trades.csv"; a.click();
}

export default function MROptions() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const poll = async () => { try { const r = await axios.get(`${API}/mr-options/status`, { timeout: 10000 }); if (r.data.success) setStatus(r.data); } catch {} };
  useEffect(() => { poll(); pollRef.current = setInterval(poll, 10000); return () => clearInterval(pollRef.current); }, []);
  const start = async () => { setLoading(true); try { await axios.post(`${API}/mr-options/start`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const stop = async () => { setLoading(true); try { await axios.post(`${API}/mr-options/stop`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const clear = async () => { if (!window.confirm("Clear closed MR-option trades?")) return; try { await axios.post(`${API}/mr-options/clear`); } catch {} setTimeout(poll, 500); };

  const trades = status?.trades || [];
  const open = trades.filter(t => t.status === "OPEN");
  const closed = trades.filter(t => t.status === "CLOSED");

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>MR-Options <span style={{ fontSize: 12, color: "#a78bfa" }}>(SENSEX 1-min RSI+BB, paper)</span></h2>
          <p className="dashboard-subtitle">Forward-validates the mean-reversion edge on REAL option premiums: fades RSI+Bollinger extremes, buys ATM weekly CE/PE, exits on index target/stop/time. P&L in ₹.</p>
        </div>
        <div className="dashboard-actions">
          <button className={`btn ${status?.active ? "btn-danger" : "btn-primary"}`} onClick={status?.active ? stop : start} disabled={loading}>
            {status?.active ? "Stop Scanner" : "Start Scanner"}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: status.active ? "var(--tint-pos)" : "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text)", flexWrap: "wrap", alignItems: "center" }}>
          <span>{status.active ? <span style={{ color: "#34d399" }}>SCANNING</span> : "STOPPED"}</span>
          <span>Checks: {status.scanCount}</span>
          {status.lastScanTime && <span>Last: {new Date(status.lastScanTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}</span>}
          <span>Market: {status.marketOpen ? <span style={{ color: "#34d399" }}>OPEN</span> : <span style={{ color: "#f87171" }}>CLOSED</span>}</span>
          <span>Open: <strong>{status.openTrades}</strong></span>
          <span>WR: <strong>{status.winRate}%</strong> ({status.wins}/{status.closed})</span>
          <span style={{ color: "var(--edge)" }}>|</span>
          <span>Booked P&L: <strong className={status.totalPnl >= 0 ? "positive" : "negative"}>{status.totalPnl > 0 ? "+" : ""}₹{status.totalPnl}</strong></span>
        </div>
      )}

      {open.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13, color: "#f59e0b" }}>Open Positions ({open.length})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Dir</th><th>Option</th><th>Strike</th><th>Qty</th><th>Entry ₹</th><th>Idx Entry</th><th>Idx Stop</th><th>Idx Target</th><th>RSI</th><th>Entry Time</th></tr></thead>
              <tbody>
                {open.map(t => (
                  <tr key={t.sig_key} className="trade-open-row">
                    <td><span className={`signal-badge ${t.side === "L" ? "badge-long" : "badge-short"}`}>{t.side === "L" ? "BULL" : "BEAR"}</span></td>
                    <td><strong style={{ color: t.opt_type === "CE" ? "#34d399" : "#f87171" }}>{t.opt_type}</strong> <span style={{ fontSize: 9, color: "var(--grey-text2)" }}>{t.symbol}</span></td>
                    <td>{t.strike}</td><td>{t.qty}</td><td><strong>{t.entry_premium}</strong></td>
                    <td>{t.entry_idx}</td><td className="sl">{t.stop_idx}</td><td style={{ color: "#34d399" }}>{t.target_idx}</td>
                    <td>{t.rsi}</td><td style={{ fontSize: 10 }}>{fmtTime(t.entry_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ padding: "8px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 13, color: "var(--grey-text)" }}>Closed Trades ({closed.length})</h4>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-export" onClick={() => exportCSV(trades)}>Export CSV</button>
            {closed.length > 0 && <button className="btn btn-sm btn-danger" onClick={clear}>Clear</button>}
          </div>
        </div>
        {closed.length > 0 && (
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Dir</th><th>Option</th><th>Strike</th><th>Qty</th><th>Entry ₹</th><th>Exit ₹</th><th>P&L ₹</th><th>Result</th><th>Exit Reason</th><th>Exit Time</th></tr></thead>
              <tbody>
                {closed.map(t => (
                  <tr key={t.sig_key} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                    <td><span className={`signal-badge ${t.side === "L" ? "badge-long" : "badge-short"}`}>{t.side === "L" ? "BULL" : "BEAR"}</span></td>
                    <td><strong style={{ color: t.opt_type === "CE" ? "#34d399" : "#f87171" }}>{t.opt_type}</strong></td>
                    <td>{t.strike}</td><td>{t.qty}</td><td>{t.entry_premium}</td><td>{t.exit_premium}</td>
                    <td className={t.pnl >= 0 ? "positive" : "negative"}>{t.pnl > 0 ? "+" : ""}₹{t.pnl}</td>
                    <td><span style={{ color: t.result === "WIN" ? "#34d399" : "#f87171", fontWeight: 700 }}>{t.result || "?"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.exit_reason}</td><td style={{ fontSize: 10 }}>{fmtTime(t.exit_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!status?.active && closed.length === 0 && open.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Click <strong>Start Scanner</strong> to forward-test the SENSEX RSI+BB mean-reversion edge on real option premiums.</p>
          <p style={{ fontSize: 12 }}>Scans SENSEX 1-min during market hours; one position at a time. Let it run ~12–15 sessions to gather a real-data verdict.</p>
        </div>
      )}
    </div>
  );
}
